import { assertUnreachable } from '@tableau/vizportalclient-common-components';
import React from 'react';
import { FlowFieldsByIdNodesFragment, FlowFieldsByIdQuery, FlowFieldsByIdQueryVariables, FlowOutputFieldSortOrder , FlowFieldsByIdDocument } from '../../../../../gen-src/queries';
import {
  ConnectionListResult,
  FeatureFlagsContext,
  GridSortProps,
  PaginatedQueryWrapper,
  SelectionProviderChildProps,
} from '../../../data-catalog-shared';
import { iconForField } from '../field/functions/FieldFunctions';
import { FieldsValidSortFields } from '../sorting/sortProviders/FieldsGridSortProvider';
import { FlowFieldsDisplayGrid, FlowFieldsDisplayGridRowData } from './FlowFieldsDisplayGrid';

export type QueryVariables = FlowFieldsByIdQueryVariables;
export type QueryResults = FlowFieldsByIdQuery;
export type QueryResultListNode = FlowFieldsByIdNodesFragment;
export type GridRowData = FlowFieldsDisplayGridRowData;

export type FlowFieldsQueryGridProps =
  SelectionProviderChildProps &
  GridSortProps<FieldsValidSortFields> & {
    readonly flowVizportalUrlId: string;
    readonly selectedOutputStepId: string;
    readonly rowCount: number | null;
  };

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  return resultData && resultData.flows && resultData.flows[0] &&
    resultData.flows[0].outputSteps[0] && resultData.flows[0].outputSteps[0].outputFieldsConnection;
}

export function mapResultNode({ id, name, ...node }: QueryResultListNode, resultData: QueryResults): GridRowData {
  const rest: GridRowData = {
    flowVizportalUrlId: resultData.flows[0]?.vizportalUrlId,
    id,
    name,
    fieldIcon: null,
    sheetCount: null,
    typeName: node.__typename,
  }

  switch (node.__typename) {
    case 'FlowFieldOutputField': return {
      ...rest,
      fieldIcon: iconForField(node.field),
      sheetCount: node.field?.downstreamSheetsConnection?.totalCount ?? null,
      labels: node.field?.__typename === 'ColumnField' ? node.field.columnsConnection?.nodes.flatMap(t => t.labels) : [],
    };
    case 'FlowColumnOutputField': return {
      ...rest,
      remoteType: node.column?.remoteType ?? undefined,
      sheetCount: node.column?.downstreamSheetsConnection?.totalCount ?? null,
      labels: node.column?.labels ? 
        node.column?.upstreamColumns?.flatMap(c => c? c.labels : []).concat(node.column?.labels) :
        node.column?.upstreamColumns?.flatMap(c => c? c.labels : [])
    };
    default:
      assertUnreachable(node);
      return { ...rest, fieldIcon: null, sheetCount: null }
  }
}

export function FlowFieldsQueryGrid(props: FlowFieldsQueryGridProps): JSX.Element {
  const { customSensitivityLabelsEnabled } = React.useContext(FeatureFlagsContext);

  return (
    <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
      queryGql={FlowFieldsByIdDocument}
      partialQueryVariables={{
        flowVizportalUrlId: props.flowVizportalUrlId,
        selectedOutputStepId: props.selectedOutputStepId,
        orderBy: props.orderBy as FlowOutputFieldSortOrder,
        customSensitivityLabelsEnabled,
      }}
      selectConnectionListResult={selectConnectionListResult}
      mapResultNode={mapResultNode}
    >
      {({ rowStore }) => (
        <FlowFieldsDisplayGrid
          rowStore={rowStore}
          selectedKeys={props.selectedKeys}
          updateSelectionForKey={props.updateSelectionForKey}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />
      )}
    </PaginatedQueryWrapper>
  );
}
